import { setContext } from '@apollo/client/link/context';
import { CognitoUserSession } from 'amazon-cognito-identity-js';

import { getCurrentSessionResolution } from 'remote/auth/get-current-session';
import {
  AuthTransitionReason,
  getAuthSessionResetContext,
  runAuthSessionTransition,
} from 'state/auth/session-transition';

import { AUTHORIZED_KEY } from './auth-required-directive';

let pendingSessionExpiration: ReturnType<typeof runAuthSessionTransition> | null = null;

const transitionExpiredSession = (invalidatedPersistedSession: boolean) => {
  if (!pendingSessionExpiration) {
    pendingSessionExpiration = runAuthSessionTransition({
      reason: AuthTransitionReason.SessionExpired,
      commitCredentials: async () => ({
        session: null,
        invalidatedPersistedSession,
      }),
    }).finally(() => {
      pendingSessionExpiration = null;
    });
  }

  return pendingSessionExpiration;
};

const contextWithSession = (
  previousContext: Record<string, unknown>,
  session: CognitoUserSession | null
) => {
  const headers =
    previousContext.headers && typeof previousContext.headers === 'object'
      ? (previousContext.headers as Record<string, unknown>)
      : {};

  if (!session) {
    const { authorization: _authorization, ...headersWithoutAuthorization } = headers;
    return {
      ...previousContext,
      headers: headersWithoutAuthorization,
      [AUTHORIZED_KEY]: false,
    };
  }

  return {
    ...previousContext,
    headers: {
      ...headers,
      authorization: `Bearer ${session.getIdToken().getJwtToken()}`,
    },
    [AUTHORIZED_KEY]: true,
  };
};

export const withAuthHeaders = setContext(async (_, previousContext) => {
  const resetContext = getAuthSessionResetContext();
  if (resetContext) {
    return contextWithSession(previousContext, resetContext.session);
  }

  const { session, invalidatedPersistedSession } = await getCurrentSessionResolution();

  if (invalidatedPersistedSession) {
    await transitionExpiredSession(true);
  }

  if (!session) {
    return previousContext;
  }

  return contextWithSession(previousContext, session);
});
